import { Main } from "@/components/Main";
import { PageHeader } from "@/components/PageHeader";
import { CreateLoaderTable } from "@/components/Loaders";
import { If } from "@/components/If";
import { useEffect, useState } from "react";
import { Percent, Plus, Pencil, Trash2, Search, ChevronUp, ChevronDown, X } from "lucide-react";
import { getSellerCommissionRules, deleteSellerCommissionRule } from "@/Api/sellerCommission";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Pagination } from "@/components/Pagination";
import { CommissionRuleFormModal } from "./FormModal";
import { toast } from "sonner";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { getUnitiesData } from "@/Api/unities";

interface CommissionRule {
  id: number;
  uuid: string;
  name: string;
  start_date: string;
  end_date: string;
  is_active: boolean;
  apply_to_all_unities: boolean;
  tiers: Array<{
    id: number;
    uuid: string;
    tier_number: number;
    min_value: string;
    max_value: string | null;
    rate_below_goal: string;
    rate_at_goal: string;
    rate_above_goal: string;
  }>;
  unities: Array<{ id: number; name: string }>;
  created_by?: { id: number; name: string };
}

interface ApiResponse {
  data: CommissionRule[];
  current_page: number;
  last_page: number;
  total: number;
}

interface Unity {
  id: number;
  uuid: string;
  name: string;
}

interface Filters {
  name: string;
  unity_id: string;
  is_active: string;
  start_date: string;
  end_date: string;
}

export const SellerCommissionRulesPage = () => {
  const [listData, setListData] = useState<ApiResponse | null>(null);
  const [state, setState] = useState<"loading" | "production">("loading");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRule, setSelectedRule] = useState<CommissionRule | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [ruleToDelete, setRuleToDelete] = useState<CommissionRule | null>(null);
  const [unities, setUnities] = useState<Unity[]>([]);

  // Filtros
  const [filters, setFilters] = useState<Filters>({
    name: "",
    unity_id: "all",
    is_active: "all",
    start_date: "",
    end_date: "",
  });
  const [isExpanded, setIsExpanded] = useState(false);

  // Ordenação
  const [sortConfig, setSortConfig] = useState<{
    column: string;
    direction: "asc" | "desc";
  } | null>(null);

  const loadUnities = async () => {
    try {
      const { data } = await getUnitiesData(1);
      setUnities(data?.response?.data || []);
    } catch (error) {
      console.error("Erro ao carregar unidades:", error);
    }
  };

  const loadData = async (page: number = 1) => {
    setState("loading");
    try {
      const apiFilters: Record<string, string | number | boolean> = {};
      if (filters.name) apiFilters.name = filters.name;
      if (filters.unity_id && filters.unity_id !== "all") apiFilters.unity_id = parseInt(filters.unity_id);
      if (filters.is_active && filters.is_active !== "all") apiFilters.is_active = filters.is_active === "true";
      if (filters.start_date) apiFilters.start_date = filters.start_date;
      if (filters.end_date) apiFilters.end_date = filters.end_date;
      if (sortConfig) {
        apiFilters.sort_by = sortConfig.column;
        apiFilters.sort_direction = sortConfig.direction;
      }

      const { data } = await getSellerCommissionRules(page, apiFilters);
      setListData(data?.response);
    } catch (error) {
      toast.error("Erro ao carregar regras de premiação");
    }
    setState("production");
  };

  useEffect(() => {
    loadUnities();
    loadData();
  }, []);

  const handleEdit = (rule: CommissionRule) => {
    setSelectedRule(rule);
    setIsModalOpen(true);
  };

  const handleDelete = async () => {
    if (!ruleToDelete) return;

    try {
      await deleteSellerCommissionRule(ruleToDelete.uuid);
      toast.success("Regra de premiação removida com sucesso");
      loadData(listData?.current_page || 1);
    } catch (error) {
      toast.error("Erro ao remover regra de premiação");
    }
    setDeleteDialogOpen(false);
    setRuleToDelete(null);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedRule(null);
  };

  const handleSuccess = () => {
    handleCloseModal();
    loadData(listData?.current_page || 1);
  };

  const formatDateStr = (dateStr: string) => {
    try {
      // Extrai apenas YYYY-MM-DD para evitar problemas de timezone
      const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (match) {
        const [, year, month, day] = match;
        return `${day}/${month}/${year}`;
      }
      return format(new Date(dateStr), "dd/MM/yyyy", { locale: ptBR });
    } catch {
      return dateStr;
    }
  };

  const handleSort = (column: string) => {
    const direction =
      sortConfig?.column === column && sortConfig?.direction === "asc"
        ? "desc"
        : "asc";
    setSortConfig({ column, direction });
  };

  const getSortIcon = (column: string) => {
    if (sortConfig?.column !== column) {
      return null;
    }
    return sortConfig?.direction === "asc" ? (
      <ChevronUp className="w-4 h-4 inline ml-1" />
    ) : (
      <ChevronDown className="w-4 h-4 inline ml-1" />
    );
  };

  const handleApplyFilters = () => {
    loadData(1);
  };

  const handleClearFilters = () => {
    setFilters({
      name: "",
      unity_id: "all",
      is_active: "all",
      start_date: "",
      end_date: "",
    });
    setSortConfig(null);
    setTimeout(() => loadData(1), 0);
  };

  const hasActiveFilters =
    filters.name ||
    (filters.unity_id && filters.unity_id !== "all") ||
    (filters.is_active && filters.is_active !== "all") ||
    filters.start_date ||
    filters.end_date;

  return (
    <Main>
      <PageHeader
        icon={Percent}
        title="Faixas de Premiação"
        description="Configure as regras e faixas de premiação dos vendedores"
      >
        <Button onClick={() => setIsModalOpen(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Nova Regra
        </Button>
      </PageHeader>

      {/* Filtros no estilo Card */}
      <Card className="mb-4">
        <CardHeader className="py-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">Filtros</CardTitle>
            <div className="flex gap-2">
              {hasActiveFilters && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleClearFilters}
                  className="h-8"
                >
                  <X className="w-4 h-4 mr-1" />
                  Limpar
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsExpanded(!isExpanded)}
                className="h-8"
              >
                {isExpanded ? "Ocultar" : "Expandir"}
              </Button>
            </div>
          </div>
        </CardHeader>

        {isExpanded && (
          <CardContent className="pt-0">
            <form onSubmit={(e) => { e.preventDefault(); handleApplyFilters(); }}>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
                <div className="space-y-2 xl:col-span-2">
                  <Label htmlFor="name">Nome</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                    <Input
                      id="name"
                      placeholder="Buscar por nome..."
                      value={filters.name}
                      onChange={(e) => setFilters({ ...filters, name: e.target.value })}
                      className="pl-10"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Unidade</Label>
                  <Select
                    value={filters.unity_id}
                    onValueChange={(value) => setFilters({ ...filters, unity_id: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Todas" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas</SelectItem>
                      {unities.map((u) => (
                        <SelectItem key={u.id} value={u.id.toString()}>
                          {u.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select
                    value={filters.is_active}
                    onValueChange={(value) => setFilters({ ...filters, is_active: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Todos" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      <SelectItem value="true">Ativa</SelectItem>
                      <SelectItem value="false">Inativa</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="start_date">Data Início</Label>
                  <Input
                    id="start_date"
                    type="date"
                    value={filters.start_date}
                    onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="end_date">Data Fim</Label>
                  <Input
                    id="end_date"
                    type="date"
                    value={filters.end_date}
                    onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                  />
                </div>
              </div>

              <div className="flex justify-end mt-4">
                <Button type="submit">
                  <Search className="w-4 h-4 mr-2" />
                  Buscar
                </Button>
              </div>
            </form>
          </CardContent>
        )}
      </Card>

      <div className="flex flex-col gap-3">
        <If condition={state === "loading"}>
          <CreateLoaderTable cols={6} lines={6} />
        </If>

        <If condition={state === "production"}>
          <Table className="bg-zinc-50 rounded dark:bg-[#2c2c2c]">
            <TableHeader>
              <TableRow>
                <TableHead
                  className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                  onClick={() => handleSort("name")}
                >
                  <div className="flex items-center gap-1">
                    <Search className="h-3 w-3 text-gray-400" />
                    Nome{getSortIcon("name")}
                  </div>
                </TableHead>
                <TableHead
                  className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                  onClick={() => handleSort("start_date")}
                >
                  <div className="flex items-center gap-1">
                    <Search className="h-3 w-3 text-gray-400" />
                    Período{getSortIcon("start_date")}
                  </div>
                </TableHead>
                <TableHead>Unidades</TableHead>
                <TableHead>Faixas</TableHead>
                <TableHead
                  className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                  onClick={() => handleSort("is_active")}
                >
                  <div className="flex items-center gap-1">
                    <Search className="h-3 w-3 text-gray-400" />
                    Status{getSortIcon("is_active")}
                  </div>
                </TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {listData?.data?.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                    Nada foi encontrado.
                  </TableCell>
                </TableRow>
              )}
              {listData?.data?.map((rule) => (
                <TableRow key={rule.uuid}>
                  <TableCell className="font-medium">{rule.name}</TableCell>
                  <TableCell>
                    {formatDateStr(rule.start_date)} - {formatDateStr(rule.end_date)}
                  </TableCell>
                  <TableCell>
                    {rule.apply_to_all_unities ? (
                      <Badge variant="secondary">Todas</Badge>
                    ) : (
                      <span className="text-sm text-muted-foreground">
                        {rule.unities?.length || 0} unidade(s)
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">{rule.tiers?.length || 0} faixa(s)</Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={rule.is_active ? "default" : "secondary"}>
                      {rule.is_active ? "Ativa" : "Inativa"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleEdit(rule)}
                      >
                        <Pencil className="w-4 h-4 text-blue-600" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          setRuleToDelete(rule);
                          setDeleteDialogOpen(true);
                        }}
                      >
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {listData && listData.last_page > 1 && (
            <Pagination
              current_page={listData.current_page}
              last_page={listData.last_page}
              handleNext={() => loadData(listData.current_page + 1)}
              handlePrevious={() => loadData(listData.current_page - 1)}
              handleLastPage={() => loadData(listData.last_page)}
            />
          )}
        </If>
      </div>

      <CommissionRuleFormModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSuccess={handleSuccess}
        rule={selectedRule}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir a regra "{ruleToDelete?.name}"?
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Main>
  );
};
